import classNames from "classnames";
import * as React from "react";

import {
  Banner,
  Field,
  RobotoButton,
  TextInput,
} from "@/shared/foundation/primitives";

import styles from "./CredentialRequestApproval.module.css";
import { CredentialRequestCard } from "./CredentialRequestCard";

export type CredentialRequestOutcomeState =
  | { kind: "pasteCode"; authorizationCode: string }
  | { kind: "returnedToCli" }
  | { kind: "denied" };

export function CredentialRequestOutcome({
  outcome,
}: {
  outcome: CredentialRequestOutcomeState;
}) {
  if (outcome.kind === "pasteCode") {
    return <PasteCode authorizationCode={outcome.authorizationCode} />;
  }

  if (outcome.kind === "returnedToCli") {
    return (
      <CredentialRequestCard title="Finishing sign-in">
        <p className={styles.body}>
          Your browser is sending the authorization code to the Roboto CLI on
          this computer.
        </p>
      </CredentialRequestCard>
    );
  }

  return (
    <CredentialRequestCard title="Sign-in denied">
      <p className={styles.body}>No access token was created.</p>
    </CredentialRequestCard>
  );
}

/**
 * Display and copy the authorization code. `CopyToClipboardButton` is not used because it sends the
 * copied text to analytics as `eventProps.link` by default. `ph-no-capture` keeps the code out of PostHog
 * session recordings, which mask only password and email inputs.
 */
function PasteCode({ authorizationCode }: { authorizationCode: string }) {
  const [copied, setCopied] = React.useState(false);

  const onCopy = React.useCallback(() => {
    navigator.clipboard.writeText(authorizationCode).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  }, [authorizationCode]);

  return (
    <CredentialRequestCard title="Paste this authorization code into your terminal">
      <Field.Root>
        <Field.Label>Authorization code</Field.Label>
        <Field.Control
          render={
            <TextInput
              className={classNames("ph-no-capture", styles.authorizationCode)}
            />
          }
          value={authorizationCode}
          readOnly
          onFocus={(event) => event.currentTarget.select()}
        />
      </Field.Root>
      <Banner variant="warning">
        Paste it only into the terminal where you started this sign-in. No one
        from Roboto will ever ask you for it. It expires within 5 minutes and
        works once.
      </Banner>
      <div className={styles.actions}>
        <RobotoButton onClick={onCopy}>
          {copied ? "Copied" : "Copy authorization code"}
        </RobotoButton>
      </div>
    </CredentialRequestCard>
  );
}
